import { useEffect, useRef, useCallback, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { FontFamily } from '@tiptap/extension-font-family';
import { Highlight } from '@tiptap/extension-highlight';
import { Superscript } from '@tiptap/extension-superscript';
import { Subscript } from '@tiptap/extension-subscript';
import { TextAlign } from '@tiptap/extension-text-align';
import { Placeholder } from '@tiptap/extension-placeholder';
import ImageResize from 'tiptap-extension-resize-image';
import { Extension } from '@tiptap/core';
import { DOMSerializer } from 'prosemirror-model';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Bold as BoldIcon,
  Italic as ItalicIcon,
  Underline as UnderlineIcon,
  Strikethrough,
  Superscript as SuperscriptIcon,
  Subscript as SubscriptIcon,
  List,
  ListOrdered,
  Outdent,
  Indent,
  TextQuote,
  Minus,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Table2,
  ImagePlus,
  RemoveFormatting,
  Sparkles,
  ChevronDown,
  AlertTriangle,
  Upload,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import type { Json } from '@/integrations/supabase/types';
import { AiEditModal } from './AiEditModal';

/* ── Custom FontSize extension ─────────────────────────────── */
const FontSize = Extension.create({
  name: 'fontSize',
  addGlobalAttributes() {
    return [
      {
        types: ['textStyle'],
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (element: HTMLElement) =>
              element.style.fontSize?.replace(/['"]+/g, '') || null,
            renderHTML: (attributes: Record<string, any>) => {
              if (!attributes.fontSize) return {};
              return { style: `font-size: ${attributes.fontSize}` };
            },
          },
        },
      },
    ];
  },
});

/* ── Special characters ────────────────────────────────────── */
const SPECIAL_CHARACTERS: Record<string, string[]> = {
  'Legal & Regulatory': ['\u00A7', '\u00B6', '\u00A9', '\u00AE', '\u2122', '\u2120'],
  'Clinical & Scientific': ['\u00B0', '\u00B5', '\u00B1', '\u00D7', '\u00F7', '\u2264', '\u2265', '\u2260', '~', '\u221E'],
  'Punctuation': ['\u2014', '\u2013', '\u2026', '\u2018', '\u2019', '\u201C', '\u201D', '\u00AB', '\u00BB'],
  'Arrows': ['\u2190', '\u2192', '\u2191', '\u2193', '\u2194', '\u21D2'],
  'Math': ['\u2211', '\u220F', '\u221A', '\u222B', '\u2202', '\u0394', '\u03C0', '\u2248'],
};

/* ── Props ─────────────────────────────────────────────────── */
interface EditorPaneProps {
  initialHtml: string;
  initialJson: Json | null;
  originalFileUrl?: string | null;
  policyId?: string | null;
  clinicId?: string | null;
  sessionId?: string | null;
  onSave: (html: string, json: Json | null) => Promise<boolean | undefined>;
  onEmptyChange: (isEmpty: boolean) => void;
}

/* ── Toolbar helpers ───────────────────────────────────────── */
function TBtn({
  onClick,
  isActive = false,
  title,
  children,
  className = '',
  disabled = false,
}: {
  onClick: () => void;
  isActive?: boolean;
  title: string;
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      disabled={disabled}
      className={`w-7 h-7 flex items-center justify-center rounded text-xs transition-colors ${
        disabled
          ? 'opacity-50 cursor-not-allowed text-muted-foreground'
          : isActive
            ? 'bg-primary/10 text-primary'
            : 'text-muted-foreground hover:bg-primary/10 hover:text-primary'
      } ${className}`}
    >
      {children}
    </button>
  );
}

function TSep() {
  return <div className="w-px h-6 bg-border mx-1" />;
}

/* ── EditorPane ────────────────────────────────────────────── */
export function EditorPane({
  initialHtml,
  initialJson,
  originalFileUrl,
  policyId,
  clinicId,
  sessionId,
  onSave,
  onEmptyChange,
}: EditorPaneProps) {
  const [wordCount, setWordCount] = useState(0);
  const [showCharPicker, setShowCharPicker] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importComplete, setImportComplete] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showReimportWarning, setShowReimportWarning] = useState(false);
  const autoSaveRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const htmlRef = useRef(initialHtml || '');
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const imageInputRef = useRef<HTMLInputElement | null>(null);

  // PDF origin detection
  const normalizedUrl = originalFileUrl?.split('?')[0] ?? '';
  const isPdfOrigin = normalizedUrl.toLowerCase().endsWith('.pdf');
  const showPdfBanner = isPdfOrigin && !initialHtml && !importComplete;

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        code: false,
        codeBlock: false,
      }),
      TextStyle,
      FontSize,
      Color,
      FontFamily,
      Highlight.configure({ multicolor: false }),
      Superscript,
      Subscript,
      TextAlign.configure({ types: ['heading', 'paragraph', 'image'] }),
      Table.configure({ resizable: true }),
      TableRow,
      TableCell,
      TableHeader,
      (ImageResize as any).configure({ inline: false, allowBase64: true }),
      Placeholder.configure({
        placeholder: showPdfBanner
          ? 'Upload the original Word document above to import content, or start writing from scratch below.'
          : 'Start writing or use Vera to help draft sections...',
      }),
    ],
    content: initialHtml || '',
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      htmlRef.current = html;
      const text = ed.getText().trim();
      setWordCount(text ? text.split(/\s+/).length : 0);
      onEmptyChange(ed.isEmpty);
    },
    onCreate: ({ editor: ed }) => {
      const text = ed.getText().trim();
      setWordCount(text ? text.split(/\s+/).length : 0);
      onEmptyChange(ed.isEmpty);
    },
  });

  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);

  /* ── Auto-save (30 s) ──────────────────────────────────── */
  useEffect(() => {
    autoSaveRef.current = setInterval(() => {
      if (htmlRef.current) {
        onSave(htmlRef.current, null);
        setLastSavedAt(new Date());
      }
    }, 30000);
    return () => {
      if (autoSaveRef.current) clearInterval(autoSaveRef.current);
    };
  }, [onSave]);

  /* ── Save on navigation away ───────────────────────────── */
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (htmlRef.current) onSave(htmlRef.current, null);
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (htmlRef.current) onSave(htmlRef.current, null);
    };
  }, [onSave]);

  /* ── Manual save + window globals ──────────────────────── */
  const manualSave = useCallback(async () => {
    const ok = await onSave(htmlRef.current, null);
    if (ok !== false) toast.success('Draft saved.');
  }, [onSave]);

  useEffect(() => {
    (window as any).__composerManualSave = manualSave;
    (window as any).__composerGetHtmlJson = () => ({ html: htmlRef.current, json: null });
    (window as any).__composerInsertContent = (html: string) => {
      editor?.chain().focus().insertContent(html).run();
    };
    return () => {
      delete (window as any).__composerManualSave;
      delete (window as any).__composerGetHtmlJson;
      delete (window as any).__composerInsertContent;
    };
  }, [manualSave, editor]);

  /* ── Special char insert ───────────────────────────────── */
  const insertSpecialChar = useCallback(
    (char: string) => {
      editor?.chain().focus().insertContent(char).run();
      setShowCharPicker(false);
    },
    [editor],
  );

  /* ── DOCX import ───────────────────────────────────────── */
  const handleDocxImport = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (fileInputRef.current) fileInputRef.current.value = '';

      const ext = file.name.split('.').pop()?.toLowerCase();
      if (ext !== 'doc' && ext !== 'docx') {
        toast.error('Please select a Word document (.doc or .docx)');
        return;
      }
      if (!clinicId || !policyId) {
        toast.error('Missing clinic or policy context.');
        return;
      }

      setImporting(true);
      toast('Importing document...');
      try {
        const storagePath = `${clinicId}/${policyId}/import_${Date.now()}.docx`;
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('policy-documents')
          .upload(storagePath, file);

        if (uploadError || !uploadData?.path) {
          throw new Error(uploadError?.message || 'Upload failed');
        }

        const { data, error } = await supabase.functions.invoke('trigger-document-to-html', {
          body: { file_path: uploadData.path },
        });

        if (error || !data?.success) {
          throw new Error(data?.error || error?.message || 'Conversion failed');
        }

        editor?.commands.setContent(data.html);
        htmlRef.current = data.html;
        setImportComplete(true);
        toast.success('Document imported. Review all formatting carefully before submitting for approval.');
      } catch (err: any) {
        console.error('DOCX import error:', err);
        toast.error('Import failed. Please try again or start writing from scratch below.');
      } finally {
        setImporting(false);
      }
    },
    [clinicId, policyId, editor],
  );

  /* ── Toolbar actions ───────────────────────────────────── */
  const handleInsertImage = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (imageInputRef.current) imageInputRef.current.value = '';
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result as string;
        (editor?.chain().focus() as any).setImage({ src: base64 }).run();
      };
      reader.readAsDataURL(file);
    },
    [editor],
  );

  const handleInsertTable = useCallback(() => {
    editor?.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  }, [editor]);

  const [aiEditAction, setAiEditAction] = useState<'improve_clarity' | 'make_more_formal' | 'shorten' | 'check_sources' | null>(null);
  const [aiEditOpen, setAiEditOpen] = useState(false);
  const [selectionRange, setSelectionRange] = useState<{ from: number; to: number } | null>(null);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [aiEditContent, setAiEditContent] = useState('');

  const getSelectionHtml = useCallback((): { html: string; from: number; to: number } | null => {
    if (!editor) return null;
    const { from, to } = editor.state.selection;
    if (editor.state.selection.empty) return null;
    const { state } = editor;
    const slice = state.doc.slice(from, to);
    const div = document.createElement('div');
    const fragment = DOMSerializer.fromSchema(state.schema).serializeFragment(slice.content);
    div.appendChild(fragment);
    return { html: div.innerHTML, from, to };
  }, [editor]);

  const triggerAiEdit = useCallback((action: 'improve_clarity' | 'make_more_formal' | 'shorten' | 'check_sources') => {
    const sel = getSelectionHtml();
    if (sel) {
      setSelectionRange({ from: sel.from, to: sel.to });
      setIsSelectionMode(true);
      setAiEditContent(sel.html);
    } else {
      setSelectionRange(null);
      setIsSelectionMode(false);
      setAiEditContent(htmlRef.current);
    }
    setAiEditAction(action);
    setAiEditOpen(true);
  }, [getSelectionHtml]);

  const handleImproveClarity = () => triggerAiEdit('improve_clarity');
  const handleMakeMoreFormal = () => triggerAiEdit('make_more_formal');
  const handleShorten = () => triggerAiEdit('shorten');
  const handleCheckSources = () => triggerAiEdit('check_sources');

  if (!editor) return null;

  return (
    <div className="flex flex-col h-full bg-card composer-editor-wrapper">
      <style>{`
        /* TipTap editor */
        .tiptap-editor .ProseMirror {
          min-height: 400px;
          padding: 16px 24px;
          outline: none;
          font-size: 14px;
          line-height: 1.7;
          color: hsl(var(--foreground));
        }
        .tiptap-editor .ProseMirror img {
          pointer-events: all !important;
          cursor: pointer;
          max-width: 100%;
        }
        .tiptap-editor .ProseMirror p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          color: hsl(var(--muted-foreground));
          pointer-events: none;
          float: left;
          height: 0;
        }
        .tiptap-editor .ProseMirror table {
          border-collapse: collapse;
          width: 100%;
          margin: 12px 0;
        }
        .tiptap-editor .ProseMirror td,
        .tiptap-editor .ProseMirror th {
          border: 1px solid hsl(var(--border));
          padding: 6px 12px;
          min-width: 60px;
        }
        .tiptap-editor .ProseMirror th {
          background: hsl(var(--muted));
          font-weight: 600;
        }
        .tiptap-editor .ProseMirror hr {
          border: none;
          border-top: 2px solid hsl(var(--border));
          margin: 1rem 0;
        }
        .tiptap-editor .ProseMirror img {
          cursor: pointer;
          max-width: 100%;
        }
        .tiptap-editor .ProseMirror h1 {
          font-size: 1.75em;
          font-weight: 700;
          margin-bottom: 0.5em;
        }
        .tiptap-editor .ProseMirror h2 {
          font-size: 1.4em;
          font-weight: 600;
          margin-bottom: 0.4em;
        }
        .tiptap-editor .ProseMirror h3 {
          font-size: 1.15em;
          font-weight: 600;
          margin-bottom: 0.3em;
        }
        .tiptap-editor .ProseMirror blockquote {
          border-left: 3px solid hsl(var(--border));
          padding-left: 1rem;
          margin-left: 0;
          color: hsl(var(--muted-foreground));
        }
        .tiptap-editor .ProseMirror ul {
          list-style-type: disc;
          padding-left: 1.5rem;
          margin: 0.5rem 0;
        }
        .tiptap-editor .ProseMirror ol {
          list-style-type: decimal;
          padding-left: 1.5rem;
          margin: 0.5rem 0;
        }
        .tiptap-editor .ProseMirror li {
          margin: 0.25rem 0;
        }
        .tiptap-editor .ProseMirror ul ul {
          list-style-type: circle;
        }
        .tiptap-editor .ProseMirror ul ul ul {
          list-style-type: square;
        }
        .tiptap-editor .ProseMirror ul,
        .tiptap-editor .ProseMirror ol {
          padding-left: 1.5rem;
        }
        .tiptap-editor .ProseMirror .tableWrapper {
          overflow-x: auto;
        }
        .tiptap-editor .ProseMirror .selectedCell {
          background: hsl(var(--primary) / 0.1);
        }
        .tiptap-editor .ProseMirror .column-resize-handle {
          position: absolute;
          right: -2px;
          top: 0;
          bottom: 0;
          width: 4px;
          background: hsl(var(--primary));
          cursor: col-resize;
        }
      `}</style>

      {/* Disclaimer banner */}
      <div className="px-6 py-2 bg-background border-b border-border flex-shrink-0">
        <p className="text-xs text-muted-foreground">
          Vera references only the documents in your Sources panel. Review all content before submitting for approval.
        </p>
      </div>

      {/* PDF origin warning banner */}
      {showPdfBanner && (
        <div className="px-6 py-3 bg-amber-50 border-b border-amber-200 flex-shrink-0 dark:bg-amber-950/30 dark:border-amber-800">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0 dark:text-amber-500" />
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                This policy was originally uploaded as a PDF. PDF content cannot be imported into the editor.
                To edit in Composer, upload the original Word document (.docx) below.
              </p>
              <Button
                variant="secondary"
                size="sm"
                className="mt-2"
                disabled={importing}
                onClick={() => setShowImportModal(true)}
              >
                <Upload className="h-4 w-4 mr-1" /> {importing ? 'Importing…' : 'Upload Word Document'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Hidden file input – always rendered so fileInputRef is never null */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".doc,.docx"
        className="hidden"
        onChange={handleDocxImport}
      />
      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleInsertImage}
      />
      {/* ── Toolbar Row 1 ──────────────────────────────────── */}
      <div className="border-b border-border bg-card px-3 py-1.5 pt-2 pl-2 flex flex-wrap items-center gap-0.5 flex-shrink-0">
        {/* Font family */}
        <select
          title="Font Family"
          className="h-7 text-xs bg-transparent border border-border rounded px-1 text-foreground"
          value={editor.getAttributes('textStyle').fontFamily || ''}
          onChange={(e) => {
            if (e.target.value) {
              editor.chain().focus().setFontFamily(e.target.value).run();
            } else {
              editor.chain().focus().unsetFontFamily().run();
            }
          }}
        >
          <option value="">Sans Serif</option>
          <option value="serif">Serif</option>
          <option value="monospace">Monospace</option>
        </select>

        {/* Font size */}
        <select
          title="Font Size"
          className="h-7 text-xs bg-transparent border border-border rounded px-1 text-foreground ml-1"
          value={editor.getAttributes('textStyle').fontSize || ''}
          onChange={(e) => {
            if (e.target.value) {
              editor.chain().focus().setMark('textStyle', { fontSize: e.target.value }).run();
            } else {
              editor
                .chain()
                .focus()
                .setMark('textStyle', { fontSize: null })
                .removeEmptyTextStyle()
                .run();
            }
          }}
        >
          <option value="">Normal</option>
          <option value="12px">Small</option>
          <option value="18px">Large</option>
          <option value="24px">Huge</option>
        </select>

        <TSep />

        {/* Text formatting */}
        <TBtn onClick={() => (editor.chain().focus() as any).toggleBold().run()} isActive={editor.isActive('bold')} title="Bold">
          <BoldIcon className="h-3.5 w-3.5" />
        </TBtn>
        <TBtn onClick={() => (editor.chain().focus() as any).toggleItalic().run()} isActive={editor.isActive('italic')} title="Italic">
          <ItalicIcon className="h-3.5 w-3.5" />
        </TBtn>
        <TBtn onClick={() => (editor.chain().focus() as any).toggleUnderline().run()} isActive={editor.isActive('underline')} title="Underline">
          <UnderlineIcon className="h-3.5 w-3.5" />
        </TBtn>
        <TBtn onClick={() => (editor.chain().focus() as any).toggleStrike().run()} isActive={editor.isActive('strike')} title="Strikethrough">
          <Strikethrough className="h-3.5 w-3.5" />
        </TBtn>

        <TSep />

        {/* Text color */}
        <label title="Text Color" className="relative w-7 h-7 flex items-center justify-center cursor-pointer">
          <span className="text-sm font-bold" style={{ color: editor.getAttributes('textStyle').color || 'currentColor' }}>A</span>
          <input
            type="color"
            className="absolute inset-0 opacity-0 cursor-pointer"
            value={editor.getAttributes('textStyle').color || '#000000'}
            onChange={(e) => (editor.chain().focus() as any).setColor(e.target.value).run()}
          />
        </label>

        {/* Highlight */}
        <label title="Highlight Color" className="relative w-7 h-7 flex items-center justify-center cursor-pointer">
          <span className="text-sm font-bold px-0.5 bg-yellow-200 rounded-sm leading-none">A</span>
          <input
            type="color"
            className="absolute inset-0 opacity-0 cursor-pointer"
            value="#ffff00"
            onChange={(e) => (editor.chain().focus() as any).toggleHighlight({ color: e.target.value }).run()}
          />
        </label>

        <TSep />

        {/* Heading */}
        <select
          title="Heading Style"
          className="h-7 text-xs bg-transparent border border-border rounded px-1 text-foreground"
          value={
            editor.isActive('heading', { level: 1 })
              ? '1'
              : editor.isActive('heading', { level: 2 })
                ? '2'
                : editor.isActive('heading', { level: 3 })
                  ? '3'
                  : ''
          }
          onChange={(e) => {
            const level = parseInt(e.target.value);
            if (level) {
              (editor.chain().focus() as any).toggleHeading({ level: level as 1 | 2 | 3 }).run();
            } else {
              (editor.chain().focus() as any).setParagraph().run();
            }
          }}
        >
          <option value="">Normal</option>
          <option value="1">Heading 1</option>
          <option value="2">Heading 2</option>
          <option value="3">Heading 3</option>
        </select>

        <TSep />

        <TBtn onClick={() => (editor.chain().focus() as any).toggleSuperscript().run()} isActive={editor.isActive('superscript')} title="Superscript">
          <SuperscriptIcon className="h-3.5 w-3.5" />
        </TBtn>
        <TBtn onClick={() => (editor.chain().focus() as any).toggleSubscript().run()} isActive={editor.isActive('subscript')} title="Subscript">
          <SubscriptIcon className="h-3.5 w-3.5" />
        </TBtn>

        <TSep />

        {/* Lists */}
        <TBtn onClick={() => (editor.chain().focus() as any).toggleOrderedList().run()} isActive={editor.isActive('orderedList')} title="Numbered List">
          <ListOrdered className="h-3.5 w-3.5" />
        </TBtn>
        <TBtn onClick={() => (editor.chain().focus() as any).toggleBulletList().run()} isActive={editor.isActive('bulletList')} title="Bullet List">
          <List className="h-3.5 w-3.5" />
        </TBtn>
        <TBtn onClick={() => editor.chain().focus().sinkListItem('listItem').run()} title="Increase Indent">
          <Indent className="h-3.5 w-3.5" />
        </TBtn>
        <TBtn onClick={() => editor.chain().focus().liftListItem('listItem').run()} title="Decrease Indent">
          <Outdent className="h-3.5 w-3.5" />
        </TBtn>

        <TSep />

        {/* Block formatting */}
        <TBtn onClick={() => (editor.chain().focus() as any).toggleBlockquote().run()} isActive={editor.isActive('blockquote')} title="Blockquote">
          <TextQuote className="h-3.5 w-3.5" />
        </TBtn>
        <TBtn onClick={() => (editor.chain().focus() as any).setHorizontalRule().run()} title="Horizontal Rule">
          <Minus className="h-3.5 w-3.5" />
        </TBtn>

        <TSep />

        {/* Text alignment */}
        <TBtn onClick={() => (editor.chain().focus() as any).setTextAlign('left').run()} isActive={editor.isActive({ textAlign: 'left' })} title="Align Left">
          <AlignLeft className="h-3.5 w-3.5" />
        </TBtn>
        <TBtn onClick={() => (editor.chain().focus() as any).setTextAlign('center').run()} isActive={editor.isActive({ textAlign: 'center' })} title="Align Center">
          <AlignCenter className="h-3.5 w-3.5" />
        </TBtn>
        <TBtn onClick={() => (editor.chain().focus() as any).setTextAlign('right').run()} isActive={editor.isActive({ textAlign: 'right' })} title="Align Right">
          <AlignRight className="h-3.5 w-3.5" />
        </TBtn>
        <TBtn onClick={() => (editor.chain().focus() as any).setTextAlign('justify').run()} isActive={editor.isActive({ textAlign: 'justify' })} title="Justify">
          <AlignJustify className="h-3.5 w-3.5" />
        </TBtn>

        <TSep />

        {/* Insert */}
        <TBtn onClick={handleInsertTable} title="Insert Table">
          <Table2 className="h-3.5 w-3.5" />
        </TBtn>
        <TBtn onClick={() => imageInputRef.current?.click()} title="Insert Image">
          <ImagePlus className="h-3.5 w-3.5" />
        </TBtn>
        <TBtn onClick={() => setShowReimportWarning(true)} title="Upload Word Document" disabled={importing}>
          <Upload className="h-3.5 w-3.5" />
        </TBtn>

        <TSep />

        {/* Clear formatting */}
        <TBtn
          onClick={() => {
            editor.chain().focus().unsetAllMarks().clearNodes().run();
          }}
          title="Clear Formatting"
        >
          <RemoveFormatting className="h-3.5 w-3.5" />
        </TBtn>

        {/* Special characters */}
        <TBtn
          onClick={() => setShowCharPicker((v) => !v)}
          title="Special Characters"
          className="!font-serif !text-base !font-semibold"
        >
          Ω
        </TBtn>
      </div>

      {/* ── Table action bar ───────────────────────────────── */}
      {editor.isActive('table') && (
        <div className="border-b border-border bg-muted/50 px-3 py-1 flex flex-wrap items-center gap-1 flex-shrink-0">
          <span className="text-[10px] font-medium text-muted-foreground mr-1 uppercase tracking-wider">Table</span>
          <Button variant="ghost" size="sm" className="h-6 text-[11px] px-1.5" onClick={() => editor.chain().focus().addRowBefore().run()}>
            Row Above
          </Button>
          <Button variant="ghost" size="sm" className="h-6 text-[11px] px-1.5" onClick={() => editor.chain().focus().addRowAfter().run()}>
            Row Below
          </Button>
          <Button variant="ghost" size="sm" className="h-6 text-[11px] px-1.5" onClick={() => editor.chain().focus().deleteRow().run()}>
            Delete Row
          </Button>
          <div className="w-px h-4 bg-border mx-0.5" />
          <Button variant="ghost" size="sm" className="h-6 text-[11px] px-1.5" onClick={() => editor.chain().focus().addColumnBefore().run()}>
            Col Left
          </Button>
          <Button variant="ghost" size="sm" className="h-6 text-[11px] px-1.5" onClick={() => editor.chain().focus().addColumnAfter().run()}>
            Col Right
          </Button>
          <Button variant="ghost" size="sm" className="h-6 text-[11px] px-1.5" onClick={() => editor.chain().focus().deleteColumn().run()}>
            Delete Col
          </Button>
          <div className="w-px h-4 bg-border mx-0.5" />
          <Button
            variant="ghost"
            size="sm"
            className="h-6 text-[11px] px-1.5 text-destructive hover:text-destructive"
            onClick={() => editor.chain().focus().deleteTable().run()}
          >
            <Trash2 className="h-3 w-3 mr-0.5" /> Delete Table
          </Button>
        </div>
      )}

      {/* ── Editor content ─────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto tiptap-editor">
        <EditorContent editor={editor} />
      </div>

      {/* ── Character picker ───────────────────────────────── */}
      {showCharPicker && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setShowCharPicker(false)} />
          <div className="absolute right-4 top-24 z-50 w-80 p-3 rounded-md border border-border bg-popover text-popover-foreground shadow-md">
            <p className="text-xs font-medium text-foreground mb-2">Special Characters</p>
            {Object.entries(SPECIAL_CHARACTERS).map(([group, chars]) => (
              <div key={group} className="mb-2">
                <p className="text-[10px] text-muted-foreground mb-1">{group}</p>
                <div className="flex flex-wrap gap-1">
                  {chars.map((char) => (
                    <button
                      key={char}
                      type="button"
                      onClick={() => insertSpecialChar(char)}
                      className="w-8 h-8 flex items-center justify-center rounded text-sm border border-border hover:bg-primary/10 hover:text-primary transition-colors"
                    >
                      {char}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ── Footer: AI Edit + Word count ───────────────────── */}
      <div className="px-4 py-1.5 border-t border-border flex-shrink-0 flex items-center justify-between">
        <div className="flex items-center gap-1">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 gap-1 px-2 text-xs">
                <Sparkles className="h-4 w-4" /> Vera Edit <ChevronDown className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuItem onClick={handleImproveClarity}>Improve clarity</DropdownMenuItem>
              <DropdownMenuItem onClick={handleMakeMoreFormal}>Make more formal</DropdownMenuItem>
              <DropdownMenuItem onClick={handleShorten}>Shorten this</DropdownMenuItem>
              <DropdownMenuItem onClick={handleCheckSources}>Check against sources</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <span className="text-xs text-muted-foreground">
          {wordCount} words
          {lastSavedAt && (
            <> · Auto-saved {lastSavedAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })}</>
          )}
        </span>
      </div>

      {/* Replace Draft Content warning modal */}
      <Dialog open={showReimportWarning} onOpenChange={setShowReimportWarning}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Replace Draft Content?</DialogTitle>
            <DialogDescription>
              This will replace your current draft content with the imported document. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setShowReimportWarning(false)}>
              Cancel
            </Button>
            <Button onClick={() => {
              setShowReimportWarning(false);
              setShowImportModal(true);
            }}>
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import confirmation modal */}
      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Before You Import</DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-3 pt-2">
                <p className="text-sm text-muted-foreground">
                  For the best import results, follow these guidelines:
                </p>
                <ul className="list-disc pl-5 space-y-1.5 text-sm text-muted-foreground">
                  <li>Use a natively built Word document (.docx) — not a PDF converted to Word</li>
                  <li>Avoid importing files exported from Google Docs, as formatting may not transfer cleanly</li>
                  <li>Complex layouts (multi-column, text boxes, decorative headers) will be simplified on import</li>
                  <li>Tables must be built using Word's Insert Table feature — tabbed or spaced "tables" will import as plain text</li>
                  <li>Images will not be imported — add them manually after import if needed</li>
                </ul>
                <p className="text-sm text-muted-foreground">
                  These guidelines help ensure your imported content is clean, accurate, and audit-ready.
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setShowImportModal(false)}>
              Cancel
            </Button>
            <Button onClick={() => {
              setShowImportModal(false);
              fileInputRef.current?.click();
            }}>
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Edit Modal */}
      <AiEditModal
        isOpen={aiEditOpen}
        action={aiEditAction}
        sessionId={sessionId ?? ''}
        documentContent={aiEditContent}
        isSelectionMode={isSelectionMode}
        onAccept={(revised) => {
          if (isSelectionMode && selectionRange) {
            editor?.chain()
              .focus()
              .deleteRange(selectionRange)
              .insertContentAt(selectionRange.from, revised)
              .run();
          } else {
            editor?.commands.setContent(revised);
          }
          setAiEditOpen(false);
          setAiEditAction(null);
          setSelectionRange(null);
          setIsSelectionMode(false);
          toast.success('Document updated.');
        }}
        onClose={() => {
          setAiEditOpen(false);
          setAiEditAction(null);
          setSelectionRange(null);
          setIsSelectionMode(false);
        }}
      />
    </div>
  );
}
